import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Select,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { graphSettingsRouteConfig } from 'src/app/graph/routes';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

const validationSchema = Yup.object().shape({
  minProposalApprovers: Yup.number().required(),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const NumberOfApproversModal = ({
  isOpen,
  onClose,
  minProposalApprovers,
}: {
  isOpen: boolean;
  onClose: () => void;
  minProposalApprovers: number;
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const { graphId } = useRouteParams(graphSettingsRouteConfig);

  const [setMinApprovers] = useMutation<
    GraphQLTypes.ProposalNumberOfApproversModalMinProposalApproversMutation,
    GraphQLTypes.ProposalNumberOfApproversModalMinProposalApproversMutationVariables
  >(gql`
    mutation ProposalNumberOfApproversModalMinProposalApproversMutation(
      $graphId: ID!
      $input: SetMinProposalApproversInput!
    ) {
      service(id: $graphId) {
        setMinProposalApprovers(input: $input) {
          ... on Service {
            id
            minProposalApprovers
          }
          ... on PermissionError {
            message
          }
          ... on ValidationError {
            message
          }
        }
      }
    }
  `);

  const {
    values,
    touched,
    isSubmitting,
    isValid,
    setSubmitting,
    setFieldTouched,
    handleBlur,
    setFieldValue,
    handleSubmit,
    resetForm,
  } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: { minProposalApprovers },
    onSubmit: async ({ minProposalApprovers: selectedMinApprovers }) => {
      setSubmitting(true);
      const { data: responseData } = await setMinApprovers({
        variables: {
          graphId,
          input: {
            minApprovers: selectedMinApprovers,
          },
        },
      });
      if (
        responseData?.service?.setMinProposalApprovers.__typename === 'Service'
      ) {
        onClose();
        resetForm({});
        setSubmitting(false);
        return;
      }
      setSubmitting(false);

      if (
        responseData?.service?.setMinProposalApprovers.__typename ===
          'PermissionError' ||
        responseData?.service?.setMinProposalApprovers.__typename ===
          'ValidationError' ||
        !responseData?.service?.setMinProposalApprovers
      ) {
        setErrorMessage(
          responseData?.service?.setMinProposalApprovers.message ??
            'An error has occurred',
        );
      } else {
        setErrorMessage(
          assertUnreachableOrReturnDefault(
            responseData.service.setMinProposalApprovers,
            'An error has occurred',
          ),
        );
      }
    },
    validationSchema,
  });

  const handleClose = useCallback(() => {
    onClose();
    resetForm({});
  }, [onClose, resetForm]);

  return (
    <Modal size="xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Select number of approvers</ModalTitle>
          <ModalDescription>
            Choose a minimum number of approvals required for every proposal on
            this graph.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <FormControl>
            <FormLabel>Minimum number of approvers</FormLabel>
            <Select
              className="w-full"
              onBlur={handleBlur}
              onChange={(e) => {
                setFieldValue(
                  'minProposalApprovers',
                  parseInt(e.target.value, 10),
                );
                setFieldTouched('minProposalApprovers');
              }}
              value={values.minProposalApprovers}
            >
              {[1, 2, 3, 4, 5, 6].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={!isValid || !touched.minProposalApprovers}
              isLoading={isSubmitting}
              loadingText="Save"
              onClick={() => handleSubmit()}
            >
              Save
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
